import React, { useEffect, useState, useRef } from 'react';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';

const STATUS_LABELS: Record<string, string> = {
  trial: 'Туршилт', active: 'Идэвхтэй', read_only: 'Зөвхөн унших',
  suspended: 'Түтгэлзүүлсэн', offboarded: 'Цуцлагдсан',
};
const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  active: { bg: '#dcfce7', color: '#166534' },
  trial: { bg: '#fef3c7', color: '#92400e' },
  suspended: { bg: '#fee2e2', color: '#dc2626' },
};
const REQ_STATUS: Record<string, { label: string; bg: string; color: string }> = {
  pending: { label: 'Хүлээгдэж буй', bg: '#fef3c7', color: '#92400e' },
  accepted: { label: 'Хүлээн авсан', bg: '#dbeafe', color: '#1e40af' },
  awaiting_files: { label: 'Файл хүлээж буй', bg: '#fef3c7', color: '#b45309' },
  approved: { label: 'Бүртгэсэн', bg: '#dcfce7', color: '#166534' },
  rejected: { label: 'Татгалзсан', bg: '#fee2e2', color: '#dc2626' },
};
const FILE_TYPE_LABELS: Record<string, string> = {
  meeting_protocol: 'Хурлын протокол',
  board_resolution: 'УЗ, ХЗ тогтоол',
  signatures_doc: 'Гарын үсэг бүхий тогтоол',
  property_doc: 'Өмчлөлийн баримт',
  decree: 'Засаг даргын захирамж',
};
const UB_DISTRICTS = ['Баянгол', 'Баянзүрх', 'Сүхбаатар', 'Чингэлтэй', 'Хан-Уул',
  'Сонгинохайрхан', 'Налайх', 'Багануур', 'Багахангай'];
const KHOROO_COUNT: Record<string, number> = {
  'Баянгол': 34, 'Баянзүрх': 43, 'Сүхбаатар': 20, 'Чингэлтэй': 24, 'Хан-Уул': 25,
  'Сонгинохайрхан': 43, 'Налайх': 8, 'Багануур': 5, 'Багахангай': 2,
};
const REP_POSITIONS = ['Иргэдийн төлөөлөгч', 'Нийслэлийн иргэдийн төлөөлөгч',
  'Дүүргийн засаг дарга', 'УИХ-ын гишүүн'];

const ROLE_LABELS: Record<string, string> = {
  director: 'Захирал', board: 'УЗ гишүүн', audit: 'ХЗ гишүүн', member: 'Гишүүн',
};

export default function DistrictPage() {
  const { user } = useAuth();
  const isPlatform = user?.role === 'platform_admin';
  // Хороо оноох эрх: платформ админ ба дүүргийн эрхтэн хоёул.
  const canAssignKhoroo = user?.role === 'platform_admin' || user?.role === 'district';
  const [tab, setTab] = useState<'tenants' | 'requests' | 'reps'>('tenants');
  const [reps, setReps] = useState<any[]>([]);
  const [repsLoading, setRepsLoading] = useState(false);
  const [showRepForm, setShowRepForm] = useState(false);
  const [repForm, setRepForm] = useState<any>({ district: '', khoroos: [] as string[], full_name: '', position: '', phone: '', email: '', bio: '' });
  const [repSaving, setRepSaving] = useState(false);
  const [editRepId, setEditRepId] = useState<string | null>(null);
  const [tenants, setTenants] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [khorooFilter, setKhorooFilter] = useState('');

  const [requests, setRequests] = useState<any[]>([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqFilter, setReqFilter] = useState('');
  const [selectedReq, setSelectedReq] = useState<any>(null);
  const [approveNote, setApproveNote] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [filesMsg, setFilesMsg] = useState('');
  const [filesList, setFilesList] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const decreeRef = useRef<HTMLInputElement>(null);
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  // Шаардлагатай баримтын жагсаалт тохируулах
  const [reqFilesModal, setReqFilesModal] = useState(false);
  const [reqFilesItems, setReqFilesItems] = useState<any[]>([]);
  const [reqFilesBusy, setReqFilesBusy] = useState(false);

  const openReqFilesConfig = () => {
    api.get('/hoa-registration/file-requirements')
      .then(res => setReqFilesItems(res?.items || []))
      .catch(() => setReqFilesItems([]))
      .finally(() => setReqFilesModal(true));
  };
  const saveReqFilesConfig = async () => {
    const items = reqFilesItems.filter((i: any) => (i.label || '').trim());
    if (items.length === 0) { alert('Дор хаяж нэг баримт оруулна уу'); return; }
    setReqFilesBusy(true);
    try {
      await api.put('/district/registration-file-requirements', { items });
      alert('Шаардлагатай баримтын жагсаалт хадгалагдлаа');
      setReqFilesModal(false);
    } catch (e: any) {
      alert('Алдаа: ' + (e?.body ? JSON.parse(e.body)?.detail || e.message : e.message));
    } finally { setReqFilesBusy(false); }
  };

  const loadTenants = (q?: string, kh?: string) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set('search', q);
    if (kh) params.set('khoroo', kh);
    const qs = params.toString() ? `?${params.toString()}` : '';
    api.get(`/district/tenants${qs}`)
      .then(res => { setTenants(res?.items || []); setTotal(res?.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const loadRequests = () => {
    setReqLoading(true);
    const qs = reqFilter ? `?status=${reqFilter}` : '';
    api.get(`/district/registration-requests${qs}`)
      .then(res => setRequests(res?.items || []))
      .catch(() => {})
      .finally(() => setReqLoading(false));
  };

  useEffect(() => { loadTenants(); loadRequests(); }, []);

  const handleSearch = () => loadTenants(search, khorooFilter);
  const khoroos = Array.from(new Set(tenants.map((t: any) => t.khoroo).filter(Boolean))).sort();

  // 1-р шат: хүсэлтийг хүлээн авах (СӨХ шууд үүсэхгүй, "хүлээн авлаа" имэйл)
  const handleAccept = async (reqId: string) => {
    setActionLoading(true);
    try {
      const fd = new FormData();
      fd.append('note', approveNote);
      const res = await fetch(`${api.baseUrl}/district/registration-requests/${reqId}/approve`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${api.getToken()}` }, body: fd,
      });
      if (!res.ok) throw new Error('Failed');
      alert('Хүсэлтийг хүлээн авлаа. Өргөдөл гаргагчид мэдэгдэл илгээгдлээ.');
      setSelectedReq(null); setApproveNote(''); loadRequests();
    } catch {
      alert('Алдаа гарлаа');
    } finally { setActionLoading(false); }
  };

  // 2-р шат: бүртгэл дуусгах — СӨХ + захирал үүсгэж нэвтрэх мэдээлэл илгээнэ
  const handleComplete = async (reqId: string) => {
    if (!window.confirm('СӨХ-ийн бүртгэлийг эцэслэн үүсгэх үү? Захиралд нэвтрэх мэдээлэл имэйлдэнэ.')) return;
    setActionLoading(true);
    try {
      const fd = new FormData();
      fd.append('note', approveNote);
      if (decreeRef.current?.files?.[0]) fd.append('decree_file', decreeRef.current.files[0]);
      const res = await fetch(`${api.baseUrl}/district/registration-requests/${reqId}/complete`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${api.getToken()}` }, body: fd,
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      alert(`Бүртгэл дууслаа!\nНэвтрэх имэйл: ${data.director_email}\nТүр нууц үг: ${data.temp_password}`);
      setSelectedReq(null); setApproveNote(''); loadRequests(); loadTenants();
    } catch {
      alert('Алдаа гарлаа');
    } finally { setActionLoading(false); }
  };

  // Тодорхой өргөдлөөс нэмэлт баримт эргэн шаардах
  const handleRequestFiles = async (reqId: string) => {
    if (!filesMsg.trim()) { alert('Ямар баримт дутуу байгааг бичнэ үү'); return; }
    setActionLoading(true);
    try {
      const labels = filesList.split(',').map(x => x.trim()).filter(Boolean);
      const fd = new FormData();
      fd.append('message', filesMsg);
      fd.append('file_labels', JSON.stringify(labels));
      const res = await fetch(`${api.baseUrl}/district/registration-requests/${reqId}/request-files`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${api.getToken()}` }, body: fd,
      });
      if (!res.ok) throw new Error('Failed');
      alert('Нэмэлт баримтын хүсэлт илгээгдлээ.');
      setSelectedReq(null); setFilesMsg(''); setFilesList(''); loadRequests();
    } catch {
      alert('Алдаа гарлаа');
    } finally { setActionLoading(false); }
  };

  const handleReject = async (reqId: string) => {
    if (!rejectNote) { alert('Татгалзах шалтгаан бичнэ үү'); return; }
    setActionLoading(true);
    try {
      const fd = new FormData();
      fd.append('note', rejectNote);
      const res = await fetch(`${api.baseUrl}/district/registration-requests/${reqId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${api.getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Failed');
      alert('Хүсэлт татгалзлаа');
      setSelectedReq(null);
      setRejectNote('');
      loadRequests();
    } catch {
      alert('Алдаа гарлаа');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadFile = (reqId: string, fileId: string, fileName: string) => {
    const token = api.getToken();
    const url = `${api.baseUrl}/district/registration-requests/${reqId}/files/${fileId}/download`;
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = fileName;
        a.click();
      });
  };

  const loadReps = () => {
    setRepsLoading(true);
    api.get('/district/representatives')
      .then((r: any) => setReps(Array.isArray(r) ? r : []))
      .catch(() => {})
      .finally(() => setRepsLoading(false));
  };

  const handleRepSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!repForm.district || !repForm.full_name) { alert('Дүүрэг, нэрийг заавал бөглөнө'); return; }
    setRepSaving(true);
    try {
      if (editRepId) {
        await api.put(`/district/representatives/${editRepId}`, canAssignKhoroo ? repForm : { ...repForm, khoroos: undefined });
      } else {
        await api.post('/district/representatives', repForm);
      }
      setShowRepForm(false);
      setEditRepId(null);
      setRepForm({ district: '', khoroos: [], full_name: '', position: '', phone: '', email: '', bio: '' });
      loadReps();
    } catch (err: any) {
      alert(err?.body ? JSON.parse(err.body)?.detail || 'Алдаа' : 'Алдаа гарлаа');
    } finally { setRepSaving(false); }
  };

  const startRepEdit = (rep: any) => {
    setEditRepId(rep.id);
    setRepForm({
      district: rep.district || '', khoroos: rep.khoroos || [],
      full_name: rep.full_name || '', position: rep.position || '',
      phone: rep.phone || '', email: rep.email || '', bio: rep.bio || '',
    });
    setShowRepForm(true);
  };

  const handleRepKhoroo = async (rep: any) => {
    const kh = window.prompt(
      `${rep.full_name} — хороодыг таслалаар тусгаарлан бичнэ (жишээ: 5,12,14).\nХоосон үлдээвэл дүүрэг даяар харагдана:`,
      (rep.khoroos || []).map((k: string) => k.replace(/[^0-9]/g, '')).join(','),
    );
    if (kh === null) return;
    const khoroos = kh.split(',').map(x => x.trim()).filter(Boolean).map(x => `${x}-р хороо`);
    try {
      await api.put(`/district/representatives/${rep.id}/khoroo`, { khoroos });
      loadReps();
    } catch (err: any) {
      alert(err?.body ? JSON.parse(err.body)?.detail || 'Алдаа' : 'Алдаа гарлаа');
    }
  };

  const handleRepDelete = async (rep: any) => {
    if (!window.confirm(`${rep.full_name}-г жагсаалтаас хасах уу?`)) return;
    try {
      await api.delete(`/district/representatives/${rep.id}`);
      loadReps();
    } catch (err: any) {
      alert(err?.body ? JSON.parse(err.body)?.detail || 'Алдаа' : 'Алдаа гарлаа');
    }
  };

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #e8e5dc' }}>
        <button onClick={() => setTab('tenants')} style={tab === 'tenants' ? tabActive : tabStyle}>
          СӨХ жагсаалт
        </button>
        <button onClick={() => { setTab('requests'); loadRequests(); }} style={tab === 'requests' ? tabActive : tabStyle}>
          Бүртгэлийн хүсэлтүүд
          {pendingCount > 0 && (
            <span style={{ marginLeft: 8, background: '#dc2626', color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
              {pendingCount}
            </span>
          )}
        </button>
        <button onClick={() => { setTab('reps'); loadReps(); }} style={tab === 'reps' ? tabActive : tabStyle}>
          Иргэдийн хурлын төлөөлөгчид
        </button>
      </div>

      {tab === 'tenants' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h2 style={{ fontFamily: "'Inter'", fontWeight: 700, color: '#1c2b22' }}>
              СӨХ-ийн нэгдсэн жагсаалт <span style={{ fontWeight: 400, fontSize: 16, color: '#5f6f66' }}>({total})</span>
            </h2>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Нэр, хаяг, регистрийн дугаар хайх..." style={{ ...inputStyle, flex: 1, maxWidth: 360 }} />
            <select value={khorooFilter} onChange={e => { setKhorooFilter(e.target.value); loadTenants(search, e.target.value); }} style={{ ...inputStyle, width: 180 }}>
              <option value="">Бүх хороо</option>
              {khoroos.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <button onClick={handleSearch} style={{ ...btnPrimary, padding: '8px 16px' }}>Хайх</button>
          </div>
          {loading ? <p>Уншиж байна...</p> : (
            <div style={card}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e8e5dc', textAlign: 'left' }}>
                    <th style={th}>СӨХ нэр</th><th style={th}>Регистр</th><th style={th}>Дүүрэг / Хороо</th>
                    <th style={th}>Байгуулагдсан</th><th style={th}>Байр</th><th style={th}>Орон сууц</th>
                    <th style={th}>СӨХ захирал</th><th style={th}>УЗ гишүүд</th><th style={th}>Төлөв</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((t: any) => (
                    <tr key={t.id} style={{ borderBottom: '1px solid #e8e5dc' }}>
                      <td style={td}>
                        <strong>{t.name}</strong>
                        {t.contact_phone && <div style={{ fontSize: 12, color: '#5f6f66' }}>{t.contact_phone}</div>}
                      </td>
                      <td style={td}>{t.registration_number}</td>
                      <td style={td}>
                        {t.district && <div>{t.district}</div>}
                        {t.khoroo && <div style={{ fontSize: 12, color: '#5f6f66' }}>{t.khoroo}</div>}
                      </td>
                      <td style={td}>{t.established_at ? new Date(t.established_at).toLocaleDateString('mn-MN') : '-'}</td>
                      <td style={td}>
                        {t.buildings?.length > 0
                          ? t.buildings.map((b: string) => (
                              <span key={b} style={{ display: 'inline-block', background: '#f0fdf4', padding: '2px 8px', borderRadius: 4, fontSize: 12, marginRight: 4, marginBottom: 2 }}>{b}</span>
                            ))
                          : <span style={{ color: '#94a3b8' }}>-</span>}
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>{t.unit_count}</td>
                      <td style={td}>
                        {t.director_name
                          ? <span style={{ fontWeight: 600, color: '#1c2b22' }}>{t.director_name}</span>
                          : <span style={{ color: '#dc2626', fontWeight: 600 }}>Захиралгүй</span>}
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {t.board_member_count > 0
                          ? <span style={{ fontWeight: 600, color: '#236844' }}>{t.board_member_count}</span>
                          : <span style={{ display: 'inline-block', minWidth: 22, padding: '2px 8px', borderRadius: 6, fontWeight: 700, background: '#fee2e2', color: '#dc2626' }}>0</span>}
                      </td>
                      <td style={td}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                          background: STATUS_COLORS[t.status]?.bg || '#f3f4f6',
                          color: STATUS_COLORS[t.status]?.color || '#374151',
                        }}>{STATUS_LABELS[t.status] || t.status}</span>
                      </td>
                    </tr>
                  ))}
                  {tenants.length === 0 && (
                    <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#94a3b8' }}>СӨХ бүртгэгдээгүй</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'requests' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontFamily: "'Inter'", fontWeight: 700, color: '#1c2b22' }}>Бүртгэлийн хүсэлтүүд</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={openReqFilesConfig} style={{ ...btnOutline, padding: '8px 14px' }} title="hoa-register маягтад шаардах баримтын жагсаалтыг тохируулах">
                ⚙ Шаардлагатай баримт
              </button>
              <select value={reqFilter} onChange={e => { setReqFilter(e.target.value); }} style={{ ...inputStyle, width: 180 }}>
                <option value="">Бүгд</option>
                <option value="pending">Хүлээгдэж буй</option>
                <option value="accepted">Хүлээн авсан</option>
                <option value="awaiting_files">Файл хүлээж буй</option>
                <option value="approved">Бүртгэсэн</option>
                <option value="rejected">Татгалзсан</option>
              </select>
            </div>
          </div>
          <button onClick={loadRequests} style={{ ...btnPrimary, marginBottom: 16, padding: '8px 16px' }}>Шинэчлэх</button>

          {/* Шаардлагатай баримт тохируулах modal */}
          {reqFilesModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
              onClick={() => setReqFilesModal(false)}>
              <div style={{ background: '#fff', borderRadius: 14, width: 560, maxWidth: '100%', maxHeight: '88vh', overflow: 'auto', padding: 24 }} onClick={e => e.stopPropagation()}>
                <h3 style={{ fontFamily: "'Inter'", fontWeight: 700, color: '#1c2b22', margin: '0 0 4px' }}>Шаардлагатай баримт</h3>
                <p style={{ fontSize: 12.5, color: '#8a978f', margin: '0 0 16px' }}>hoa-register маягтад бүртгүүлэгч оруулах баримтын жагсаалт. «Заавал» тэмдэглэвэл тэр баримтгүйгээр илгээх боломжгүй.</p>
                <div style={{ display: 'grid', gap: 8 }}>
                  {reqFilesItems.map((it: any, i: number) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input value={it.label || ''} onChange={e => setReqFilesItems(reqFilesItems.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                        placeholder="Баримтын нэр" style={{ ...inputStyle, flex: 1 }} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#5f6f66', whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={!!it.required} onChange={e => setReqFilesItems(reqFilesItems.map((x, j) => j === i ? { ...x, required: e.target.checked } : x))} />
                        Заавал
                      </label>
                      <button onClick={() => setReqFilesItems(reqFilesItems.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16 }}>✕</button>
                    </div>
                  ))}
                </div>
                <button onClick={() => setReqFilesItems([...reqFilesItems, { key: `doc_${Date.now()}`, label: '', required: false }])}
                  style={{ ...btnOutline, marginTop: 10, padding: '6px 14px', fontSize: 13 }}>+ Баримт нэмэх</button>
                <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
                  <button onClick={() => setReqFilesModal(false)} style={{ background: '#e8e5dc', color: '#2a3b32', border: 'none', padding: '8px 18px', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>Болих</button>
                  <button onClick={saveReqFilesConfig} disabled={reqFilesBusy} style={{ ...btnPrimary, padding: '8px 18px', opacity: reqFilesBusy ? 0.6 : 1 }}>{reqFilesBusy ? 'Хадгалж байна...' : 'Хадгалах'}</button>
                </div>
              </div>
            </div>
          )}

          {reqLoading ? <p>Уншиж байна...</p> : (
            <>
              {/* Detail modal */}
              {selectedReq && (
                <div onClick={() => setSelectedReq(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                  <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, maxWidth: 700, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, position: 'sticky', top: -28, background: '#fff', paddingTop: 28, marginTop: -28, zIndex: 2 }}>
                      <h3 style={{ fontFamily: "'Inter'", fontWeight: 700, color: '#1c2b22', margin: 0 }}>{selectedReq.org_name}</h3>
                      <button onClick={() => setSelectedReq(null)} title="Хаах" style={{ background: '#f0ede4', border: 'none', fontSize: 18, cursor: 'pointer', width: 34, height: 34, borderRadius: 8, color: '#5f6f66', lineHeight: 1 }}>✕</button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                      <div><span style={detailLabel}>Регистр:</span> {selectedReq.registration_number}</div>
                      <div><span style={detailLabel}>Хаяг:</span> {selectedReq.address || '-'}</div>
                      <div><span style={detailLabel}>Утас:</span> {selectedReq.contact_phone}</div>
                      <div><span style={detailLabel}>И-мэйл:</span> {selectedReq.contact_email}</div>
                      <div><span style={detailLabel}>Дүүрэг:</span> {selectedReq.district || '-'}</div>
                      <div><span style={detailLabel}>Хороо:</span> {selectedReq.khoroo || '-'}</div>
                      <div><span style={detailLabel}>Огноо:</span> {selectedReq.created_at ? new Date(selectedReq.created_at).toLocaleDateString('mn-MN') : '-'}</div>
                      <div>
                        <span style={detailLabel}>Төлөв:</span>{' '}
                        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: REQ_STATUS[selectedReq.status]?.bg, color: REQ_STATUS[selectedReq.status]?.color }}>
                          {REQ_STATUS[selectedReq.status]?.label || selectedReq.status}
                        </span>
                      </div>
                    </div>

                    {/* Board members */}
                    {selectedReq.board_members?.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        <h4 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 14, color: '#1c2b22', marginBottom: 10 }}>УЗ, ХЗ гишүүд</h4>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e8e5dc' }}>
                              <th style={th}>Нэр</th><th style={th}>Албан тушаал</th><th style={th}>Утас</th><th style={th}>И-мэйл</th><th style={th}>Регистр</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedReq.board_members.map((m: any, i: number) => (
                              <tr key={i} style={{ borderBottom: '1px solid #f0ede4' }}>
                                <td style={td}>{m.name}</td>
                                <td style={td}>{ROLE_LABELS[m.role] || m.role}</td>
                                <td style={td}>{m.phone}</td>
                                <td style={td}>{m.email || '-'}</td>
                                <td style={td}>{m.register || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Files */}
                    {selectedReq.files?.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        <h4 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 14, color: '#1c2b22', marginBottom: 10 }}>Хавсаргасан файлууд</h4>
                        <div style={{ display: 'grid', gap: 8 }}>
                          {selectedReq.files.map((f: any) => (
                            <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fbfaf6', padding: '10px 14px', borderRadius: 8, border: '1px solid #e8e5dc' }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1c2b22' }}>{FILE_TYPE_LABELS[f.file_type] || f.file_type}</div>
                                <div style={{ fontSize: 12, color: '#5f6f66' }}>{f.file_name} ({Math.round(f.file_size / 1024)} KB)</div>
                              </div>
                              <button onClick={() => downloadFile(selectedReq.id, f.id, f.file_name)} style={{ ...btnOutline, padding: '6px 12px', fontSize: 12 }}>Татах</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions for pending: хүлээн авах / нэмэлт файл / татгалзах */}
                    {selectedReq.status === 'pending' && (
                      <div style={{ borderTop: '2px solid #e8e5dc', paddingTop: 20, display: 'grid', gap: 20 }}>
                        <div>
                          <h4 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 14, color: '#166534', marginBottom: 8 }}>✅ Хүлээн авах</h4>
                          <p style={{ fontSize: 12.5, color: '#8a978f', margin: '0 0 8px' }}>
                            Өргөдөл гаргагчид «Хүсэлтийг хүлээн авлаа, журмын дагуу бүртгэл дууссаны дараа хариу өгнө» гэж имэйлдэнэ. СӨХ дараа үүснэ.
                          </p>
                          <textarea value={approveNote} onChange={e => setApproveNote(e.target.value)} placeholder="Тэмдэглэл (заавал биш)" style={{ ...inputStyle, minHeight: 50, resize: 'vertical', marginBottom: 8 }} />
                          <button onClick={() => handleAccept(selectedReq.id)} disabled={actionLoading} style={{ ...btnPrimary, padding: '10px 24px', opacity: actionLoading ? 0.6 : 1 }}>
                            {actionLoading ? 'Илгээж байна...' : 'Хүлээн авах'}
                          </button>
                        </div>
                        <div>
                          <h4 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 14, color: '#b45309', marginBottom: 8 }}>📎 Нэмэлт баримт шаардах</h4>
                          <input value={filesList} onChange={e => setFilesList(e.target.value)} placeholder="Шаардах баримтууд (таслалаар, жнь: Хурлын протокол, Өмчлөлийн баримт)" style={{ ...inputStyle, marginBottom: 8 }} />
                          <textarea value={filesMsg} onChange={e => setFilesMsg(e.target.value)} placeholder="Тайлбар мессеж *" style={{ ...inputStyle, minHeight: 50, resize: 'vertical', marginBottom: 8 }} />
                          <button onClick={() => handleRequestFiles(selectedReq.id)} disabled={actionLoading} style={{ background: '#b45309', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: actionLoading ? 0.6 : 1 }}>
                            Нэмэлт баримт шаардах
                          </button>
                        </div>
                        <div>
                          <h4 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 14, color: '#dc2626', marginBottom: 8 }}>✖ Татгалзах (буцаах)</h4>
                          <textarea value={rejectNote} onChange={e => setRejectNote(e.target.value)} placeholder="Татгалзах шалтгаан * (имэйлээр илгээгдэнэ)" style={{ ...inputStyle, minHeight: 50, resize: 'vertical', marginBottom: 8 }} />
                          <button onClick={() => handleReject(selectedReq.id)} disabled={actionLoading} style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: actionLoading ? 0.6 : 1 }}>
                            Татгалзах
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Accepted: бүртгэл дуусгах (СӨХ үүсгэх) */}
                    {selectedReq.status === 'accepted' && (
                      <div style={{ borderTop: '2px solid #e8e5dc', paddingTop: 20 }}>
                        <h4 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 14, color: '#166534', marginBottom: 8 }}>🏁 Бүртгэл дуусгах</h4>
                        <p style={{ fontSize: 12.5, color: '#8a978f', margin: '0 0 8px' }}>
                          СӨХ + захирлын бүртгэл үүсч, захиралд нэвтрэх мэдээлэл имэйлдэнэ.
                        </p>
                        <div style={{ marginBottom: 8 }}>
                          <label style={{ fontSize: 13, color: '#5f6f66', display: 'block', marginBottom: 4 }}>Засаг даргын захирамж (файл, заавал биш)</label>
                          <input ref={decreeRef} type="file" accept=".pdf,.doc,.docx" style={{ fontSize: 13 }} />
                        </div>
                        <textarea value={approveNote} onChange={e => setApproveNote(e.target.value)} placeholder="Тэмдэглэл (заавал биш)" style={{ ...inputStyle, minHeight: 50, resize: 'vertical', marginBottom: 8 }} />
                        <button onClick={() => handleComplete(selectedReq.id)} disabled={actionLoading} style={{ ...btnPrimary, padding: '10px 24px', opacity: actionLoading ? 0.6 : 1 }}>
                          {actionLoading ? 'Бүртгэж байна...' : 'Бүртгэл дуусгах'}
                        </button>
                      </div>
                    )}

                    {selectedReq.status === 'awaiting_files' && (
                      <div style={{ borderTop: '2px solid #e8e5dc', paddingTop: 20, background: '#fffbeb', borderRadius: 8, padding: 14, marginTop: 12 }}>
                        <span style={{ fontSize: 13.5, color: '#92400e' }}>📎 Нэмэлт баримт хүсэлт илгээгдсэн — өргөдөл гаргагчийн файл хүлээгдэж байна.</span>
                      </div>
                    )}

                    {selectedReq.reviewer_note && selectedReq.status !== 'pending' && (
                      <div style={{ background: '#f0fdf4', borderRadius: 8, padding: 12, marginTop: 12 }}>
                        <span style={{ fontSize: 13, color: '#5f6f66' }}>Тэмдэглэл: </span>
                        <span style={{ fontSize: 14, color: '#1c2b22' }}>{selectedReq.reviewer_note}</span>
                      </div>
                    )}

                    <div style={{ borderTop: '1px solid #e8e5dc', marginTop: 20, paddingTop: 16, textAlign: 'right' }}>
                      <button onClick={() => setSelectedReq(null)} style={{ background: '#e8e5dc', color: '#2a3b32', border: 'none', padding: '10px 28px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>← Буцах</button>
                    </div>
                  </div>
                </div>
              )}

              <div style={card}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e8e5dc', textAlign: 'left' }}>
                      <th style={th}>СӨХ нэр</th><th style={th}>Регистр</th><th style={th}>Дүүрэг</th>
                      <th style={th}>И-мэйл</th><th style={th}>Файлууд</th><th style={th}>Огноо</th><th style={th}>Төлөв</th><th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.filter(r => !reqFilter || r.status === reqFilter).map((r: any) => (
                      <tr key={r.id} style={{ borderBottom: '1px solid #e8e5dc' }}>
                        <td style={td}><strong>{r.org_name}</strong></td>
                        <td style={td}>{r.registration_number}</td>
                        <td style={td}>{r.district || '-'}</td>
                        <td style={td}>{r.contact_email}</td>
                        <td style={{ ...td, textAlign: 'center' }}>{r.files?.length || 0}</td>
                        <td style={td}>{r.created_at ? new Date(r.created_at).toLocaleDateString('mn-MN') : '-'}</td>
                        <td style={td}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                            background: REQ_STATUS[r.status]?.bg, color: REQ_STATUS[r.status]?.color,
                          }}>{REQ_STATUS[r.status]?.label || r.status}</span>
                        </td>
                        <td style={td}>
                          <button onClick={() => { setSelectedReq(r); setApproveNote(''); setRejectNote(''); setFilesMsg(''); setFilesList(''); }} style={{ ...btnOutline, padding: '4px 12px', fontSize: 12 }}>Дэлгэрэнгүй</button>
                        </td>
                      </tr>
                    ))}
                    {requests.length === 0 && (
                      <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#94a3b8' }}>Хүсэлт байхгүй</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* Иргэдийн хурлын төлөөлөгчид — мобайлын Дүүрэг хуудсанд харагдана */}
      {tab === 'reps' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <p style={{ margin: 0, fontSize: 13, color: '#5f6f66' }}>
              Энд оруулсан мэдээлэл оршин суугчдын мобайл аппын "Дүүрэг" хуудсанд харагдана.
              {!canAssignKhoroo && ' Хорооны харьяаллыг зөвхөн дүүрэг/платформ админ оноодог.'}
            </p>
            <button onClick={() => { setEditRepId(null); setRepForm({ district: '', khoroos: [], full_name: '', position: '', phone: '', email: '', bio: '' }); setShowRepForm(true); }} style={{
              background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none',
              padding: '8px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14,
            }}>+ Төлөөлөгч нэмэх</button>
          </div>

          {repsLoading ? <p>Уншиж байна...</p> : (
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              {reps.length === 0 ? (
                <p style={{ color: '#5f6f66', textAlign: 'center', padding: 24 }}>Төлөөлөгч бүртгэгдээгүй байна.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e8e5dc', textAlign: 'left' }}>
                      {['Нэр', 'Дүүрэг', 'Хороо', 'Албан тушаал', 'Утас', 'И-мэйл', ''].map((h, i) => (
                        <th key={i} style={{ padding: '10px 12px', fontSize: 13, color: '#5f6f66', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reps.map((r: any) => (
                      <tr key={r.id} style={{ borderBottom: '1px solid #e8e5dc' }}>
                        <td style={repTd}><strong>{r.full_name}</strong></td>
                        <td style={repTd}>{r.district}</td>
                        <td style={repTd}>{(r.khoroos && r.khoroos.length > 0) ? r.khoroos.join(', ') : <span style={{ color: '#b8c0ba' }}>Дүүрэг даяар</span>}</td>
                        <td style={repTd}>{r.position || '-'}</td>
                        <td style={repTd}>{r.phone || '-'}</td>
                        <td style={repTd}>{r.email || '-'}</td>
                        <td style={repTd}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => startRepEdit(r)} title="Засах"
                              style={{ background: 'none', border: 'none', color: '#236844', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Засах</button>
                            {canAssignKhoroo && (
                              <button onClick={() => handleRepKhoroo(r)} title="Хороо оноох"
                                style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 13 }}>Хороо</button>
                            )}
                            {isPlatform && (
                              <button onClick={() => handleRepDelete(r)} title="Устгах"
                                style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13 }}>Устгах</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {showRepForm && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
              onClick={() => { setShowRepForm(false); setEditRepId(null); }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 520, maxWidth: '92%' }} onClick={ev => ev.stopPropagation()}>
                <h3 style={{ margin: '0 0 16px', fontWeight: 700, color: '#1c2b22' }}>{editRepId ? 'Төлөөлөгч засах' : 'Төлөөлөгч нэмэх'}</h3>
                <form onSubmit={handleRepSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={repLabel}>Овог нэр *</label>
                    <input value={repForm.full_name} onChange={e => setRepForm({ ...repForm, full_name: e.target.value })} style={repInput} required />
                  </div>
                  <div>
                    <label style={repLabel}>Дүүрэг *</label>
                    <select value={repForm.district}
                      onChange={e => setRepForm({ ...repForm, district: e.target.value, khoroos: [] })}
                      style={repInput} required>
                      <option value="">-- Дүүрэг сонгох --</option>
                      {UB_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={repLabel}>Албан тушаал *</label>
                    <select value={repForm.position}
                      onChange={e => setRepForm({ ...repForm, position: e.target.value })}
                      style={repInput} required>
                      <option value="">-- Сонгох --</option>
                      {REP_POSITIONS.map(pos => <option key={pos} value={pos}>{pos}</option>)}
                    </select>
                  </div>
                  {canAssignKhoroo && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={repLabel}>
                          Харьяалагдах хороод — нэг буюу хэд хэдэн сонгож болно
                          ({repForm.khoroos.length} сонгосон, хоосон бол дүүрэг даяар)
                        </label>
                        {repForm.district && (
                          <button type="button"
                            onClick={() => {
                              const all = Array.from({ length: KHOROO_COUNT[repForm.district] || 43 }, (_, i) => `${i + 1}-р хороо`);
                              const full = repForm.khoroos.length === all.length;
                              setRepForm({ ...repForm, khoroos: full ? [] : all });
                            }}
                            style={{ background: 'none', border: 'none', color: '#236844', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>
                            {repForm.khoroos.length === (KHOROO_COUNT[repForm.district] || 43) ? 'Цэвэрлэх' : 'Бүгдийг сонгох'}
                          </button>
                        )}
                      </div>
                      {!repForm.district ? (
                        <div style={{ padding: '10px 12px', border: '1.5px dashed #dcdacf', borderRadius: 10, background: '#fbfaf6', color: '#8a9690', fontSize: 13 }}>
                          Эхлээд дүүрэг сонгоно уу — тухайн дүүргийн хороод энд гарч ирнэ.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 130, overflowY: 'auto', padding: 8, border: '1.5px solid #dcdacf', borderRadius: 10, background: '#fbfaf6' }}>
                          {Array.from({ length: KHOROO_COUNT[repForm.district] || 43 }, (_, i) => `${i + 1}-р хороо`).map(kh => {
                            const on = repForm.khoroos.includes(kh);
                            return (
                              <button type="button" key={kh}
                                onClick={() => setRepForm({
                                  ...repForm,
                                  khoroos: on ? repForm.khoroos.filter((x: string) => x !== kh) : [...repForm.khoroos, kh],
                                })}
                                style={{
                                  padding: '4px 10px', borderRadius: 8, fontSize: 12.5, cursor: 'pointer',
                                  border: on ? '1.5px solid #236844' : '1.5px solid #dcdacf',
                                  background: on ? '#eaf3ec' : '#fff',
                                  color: on ? '#236844' : '#5f6f66', fontWeight: on ? 700 : 400,
                                }}>
                                {kh.replace('-р хороо', '')}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <label style={repLabel}>Утас</label>
                    <input value={repForm.phone} onChange={e => setRepForm({ ...repForm, phone: e.target.value })} style={repInput} />
                  </div>
                  <div>
                    <label style={repLabel}>И-мэйл</label>
                    <input value={repForm.email} onChange={e => setRepForm({ ...repForm, email: e.target.value })} style={repInput} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={repLabel}>Танилцуулга</label>
                    <textarea value={repForm.bio} onChange={e => setRepForm({ ...repForm, bio: e.target.value })} style={{ ...repInput, minHeight: 70, resize: 'vertical' }} />
                  </div>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
                    <button type="submit" disabled={repSaving} style={{
                      background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none',
                      padding: '8px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14,
                      opacity: repSaving ? 0.7 : 1,
                    }}>{repSaving ? 'Хадгалж байна...' : 'Хадгалах'}</button>
                    <button type="button" onClick={() => setShowRepForm(false)} style={{
                      background: '#e8e5dc', color: '#2a3b32', border: 'none', padding: '8px 20px',
                      borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14,
                    }}>Болих</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none', padding: '8px 20px',
  borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: "'Inter'",
};
const btnOutline: React.CSSProperties = {
  background: 'transparent', color: '#236844', border: '1.5px solid #236844',
  padding: '8px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: "'Inter'",
};
const card: React.CSSProperties = {
  background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
const inputStyle: React.CSSProperties = {
  padding: '8px 12px', border: '1.5px solid #dcdacf', borderRadius: 10, fontSize: 14, boxSizing: 'border-box', background: '#fbfaf6', width: '100%',
};
const th: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: '#5f6f66', fontWeight: 600 };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 14, color: '#1c2b22' };
const detailLabel: React.CSSProperties = { fontSize: 13, color: '#5f6f66', fontWeight: 500 };
const repTd: React.CSSProperties = { padding: '10px 12px', fontSize: 14, color: '#1c2b22' };
const repLabel: React.CSSProperties = { display: 'block', marginBottom: 4, fontSize: 13, color: '#2a3b32', fontWeight: 500 };
const repInput: React.CSSProperties = { width: '100%', padding: '9px 12px', border: '1.5px solid #dcdacf', borderRadius: 10, fontSize: 14, boxSizing: 'border-box', background: '#fbfaf6' };

const tabStyle: React.CSSProperties = {
  padding: '10px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
  fontWeight: 600, fontSize: 14, color: '#5f6f66', borderBottom: '2px solid transparent', marginBottom: -2,
};
const tabActive: React.CSSProperties = {
  ...tabStyle, color: '#236844', borderBottom: '2px solid #236844',
};
